/**
 * Create / edit page for an HTTP header rewrite (edge) rule, modelled on the
 * origin/firewall rule builder. Reached from the list via `edge-rules/new?domain=`
 * and `edge-rules/edit?domain=&id=`.
 *
 * Edge rules have no schema endpoint — the condition vocabulary is identical to
 * firewall rules, so the builder is driven by `GET /waf/firewall/schema`. The
 * action is chosen from a THEN pill set (Redirect / Replace request header /
 * Replace response header / Force HTTPS / Replace (legacy)). Supported header
 * names and dynamic value tokens come from `/waf/edge/replace-headers` and
 * `/waf/edge/dynamic-values`.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Replace, ArrowLeft, Trash2, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  IconButton,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafFirewallSchema,
  useWafEdgeRules,
  useWafEdgeTemplates,
  useWafEdgeReplaceHeaders,
  useWafEdgeDynamicValues,
  useCreateEdgeRule,
  useUpdateEdgeRule,
  wafExtractError,
  type EdgeRule,
  type EdgeHeaderValue,
  type EdgeRuleAction,
  type EdgeRuleCondition,
  type EdgeRuleInput,
  type FirewallField,
  type FirewallOperator,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { MultiSelect, type MultiSelectOption } from '../_shared/MultiSelect';
import { geoCountries, humanizeGeoName, acGeoCode } from '../_shared/geo';
import styles from './WafEdgeRules.module.css';

/** Country options for geo operators (value = "{continent}-{country}-00-00-00"). */
const GEO_OPTIONS: MultiSelectOption[] = geoCountries
  .map((c) => ({ value: acGeoCode(c), label: humanizeGeoName(c.name) }))
  .sort((a, b) => a.label.localeCompare(b.label));

/* ---- Condition value (IP) validation, shared with the firewall builder ---- */

/** The "Remote address" / "Real client IP" fields expect an IP value. */
const fieldIsIp = (f?: FirewallField) => !!f && /remote|client|ip[\s_-]?addr/i.test(`${f.value} ${f.label}`);

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

/** A plain IPv4 address, optionally with a CIDR prefix (/0–32). */
function isIpAddress(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  const slash = s.indexOf('/');
  if (slash === -1) return IPV4.test(s);
  const addr = s.slice(0, slash);
  const prefix = s.slice(slash + 1);
  return IPV4.test(addr) && /^\d{1,2}$/.test(prefix) && Number(prefix) <= 32;
}

function isUrlish(raw: string): boolean {
  try {
    return !!new URL(raw.trim());
  } catch {
    return false;
  }
}

/**
 * Free-form list operators (multi-value, no fixed options) — mirror the SDK's
 * `fwListOps`. Their value is a typed list of strings stored under `values`.
 */
const LIST_OPERATORS = new Set(['pm', '!pm', 'isIn', '!isIn']);
const opIsList = (op?: FirewallOperator) => !!op && LIST_OPERATORS.has(op.value);

/* ----------------------------- Action constants ----------------------------- */

/** THEN action-type pills. `wire` is the action `type` the backend receives. */
const ACTION_PILLS = [
  { value: 'redirect', label: 'Redirect' },
  { value: 'replace_request', label: 'Replace request header' },
  { value: 'replace_response', label: 'Replace response header' },
  { value: 'force_https', label: 'Force HTTPS' },
  { value: 'replace_legacy', label: 'Replace (legacy)' },
] as const;
type ActionKind = (typeof ACTION_PILLS)[number]['value'];

const HEADER_MODES = [
  { value: 'static', label: 'Set static value' },
  { value: 'dynamic', label: 'Set dynamic value' },
  { value: 'remove', label: 'Remove header' },
];

const HEADER_SIDES = [
  { value: 'request', label: 'Request' },
  { value: 'response', label: 'Response' },
];

const REDIRECT_CODES = ['301', '302', '303', '307', '308'];
const REDIRECT_TYPES = [
  { value: 'static', label: 'Static' },
  { value: 'dynamic', label: 'Dynamic' },
];

const isReplaceKind = (k: ActionKind) =>
  k === 'replace_request' || k === 'replace_response' || k === 'replace_legacy';
/** Which side header rows of a replace kind apply to. */
const headerSide = (k: ActionKind) => (k === 'replace_response' ? 'response' : 'request');

/**
 * One header-rewrite row. `mode` decides how `value` is supplied/sent (request/
 * response pills). `side` is the per-row request/response choice used only by the
 * legacy variant; the request/response pills ignore it and force the side.
 */
interface HeaderRow {
  mode: string; // static | dynamic | remove
  name: string;
  value: string;
  side: string; // request | response (legacy only)
}
const blankHeader = (): HeaderRow => ({ mode: 'static', name: '', value: '', side: 'request' });

/** One condition row in the builder. `logic` connects to the NEXT row. */
interface Row {
  field: string;
  key: string;
  operator: string;
  value: string;
  values: string[];
  logic: string;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint ? <span className={styles.subHint}>{hint}</span> : null}
    </div>
  );
}

export function WafEdgeRuleForm({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [params] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const id = params.get('id');
  const isEdit = !!id;
  const back = () => navigate(`/services/waf/${serviceId}/edge-rules`);
  const headerListId = useId();

  const schemaQ = useWafFirewallSchema(serviceId);
  const fields = schemaQ.data?.fields ?? [];
  const replaceHeadersQ = useWafEdgeReplaceHeaders(serviceId);
  const supportNames = replaceHeadersQ.data?.support ?? [];
  const unsupportSet = useMemo(
    () => new Set((replaceHeadersQ.data?.unsupport ?? []).map((n) => n.toLowerCase())),
    [replaceHeadersQ.data],
  );
  const dynamicValuesQ = useWafEdgeDynamicValues(serviceId);
  const dynamicValues = dynamicValuesQ.data ?? [];
  // Create + ?template=<id> → clone that template's name/action/conditions.
  const templateId = params.get('template') ?? '';
  const wantTemplate = !isEdit && !!templateId;
  const templatesQ = useWafEdgeTemplates(wantTemplate ? serviceId : undefined);
  const template = useMemo(
    () => (wantTemplate ? (templatesQ.data ?? []).find((t) => t.id === templateId) : undefined),
    [wantTemplate, templatesQ.data, templateId],
  );
  const dynamicSet = useMemo(() => new Set(dynamicValues), [dynamicValues]);

  /** Infer a header row's edit mode — trust `expression`, else fall back to the value. */
  const modeOf = (h: EdgeHeaderValue): string => {
    if (h.expression === 'dynamic') return 'dynamic';
    if (h.expression === 'remove') return 'remove';
    if (h.expression === 'static') return 'static';
    if (h.value === '') return 'remove';
    return dynamicSet.has(h.value) ? 'dynamic' : 'static';
  };

  const rulesQ = useWafEdgeRules(serviceId, isEdit ? domain : undefined);
  const rule = useMemo<EdgeRule | undefined>(
    () => (isEdit ? (rulesQ.data ?? []).find((r) => r.id === id) : undefined),
    [isEdit, rulesQ.data, id],
  );

  const fieldFor = (f: string): FirewallField | undefined => fields.find((x) => x.value === f);
  const opFor = (r: Row): FirewallOperator | undefined => fieldFor(r.field)?.operators.find((o) => o.value === r.operator);

  const blankRow = (): Row => {
    const f0 = fields[0];
    const o0 = f0?.operators[0];
    return { field: f0?.value ?? '', key: '', operator: o0?.value ?? '', value: '', values: [], logic: 'and' };
  };

  // ---- form state ----
  const [seeded, setSeeded] = useState(false);
  const [name, setName] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  // action
  const [actionKind, setActionKind] = useState<ActionKind>('replace_request');
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>([blankHeader()]);
  // redirect sub-fields
  const [redirectUrl, setRedirectUrl] = useState('');
  const [redirectCode, setRedirectCode] = useState('302');
  const [redirectType, setRedirectType] = useState('static');
  const [preserveQuery, setPreserveQuery] = useState(true);
  // Validation hints stay hidden until the first submit attempt.
  const [submitted, setSubmitted] = useState(false);

  // Seed once the schema (and, on edit, the rule) is ready.
  useEffect(() => {
    if (seeded) return;
    if (schemaQ.isLoading || fields.length === 0) return;
    if (isEdit) {
      if (rulesQ.isLoading) return;
      if (rule) seedFrom(rule);
      setSeeded(true);
    } else if (wantTemplate) {
      if (templatesQ.isLoading) return;
      if (template) seedFrom(template);
      else setRows([blankRow()]);
      setSeeded(true);
    } else {
      setRows([blankRow()]);
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeded, schemaQ.isLoading, fields.length, isEdit, rule, rulesQ.isLoading, wantTemplate, template, templatesQ.isLoading]);

  function seedFrom(r: { name: string; conditions?: EdgeRuleCondition[]; action?: EdgeRuleAction }) {
    setName(r.name);
    const seededRows: Row[] = (r.conditions ?? []).map((c) => ({
      field: c.field,
      key: c.key ?? '',
      operator: c.operator,
      value: c.value ?? '',
      values: c.values ?? [],
      logic: c.logic || 'and',
    }));
    setRows(seededRows.length ? seededRows : [blankRow()]);

    const a = r.action ?? { type: '' };
    if (a.type === 'redirect') {
      setActionKind('redirect');
      setRedirectUrl(a.url ?? '');
      setRedirectCode(a.code ? String(a.code) : '302');
      setRedirectType(a.redirect_type || 'static');
      setPreserveQuery(a.preserve_query_string ?? true);
    } else if (a.type === 'forceHttps') {
      setActionKind('force_https');
    } else {
      // type === 'replace'. Mixed header sides → legacy (per-row side); a uniform
      // side opens in the matching request/response pill (with static/dynamic/remove).
      const hs = a.headers ?? [];
      const sides = new Set(hs.map((h) => (h.type === 'response' ? 'response' : 'request')));
      if (sides.size > 1) {
        setActionKind('replace_legacy');
        setHeaderRows(
          hs.map<HeaderRow>((h) => ({
            mode: 'static',
            name: h.name ?? '',
            value: h.value ?? '',
            side: h.type === 'response' ? 'response' : 'request',
          })),
        );
      } else {
        const side = hs[0]?.type === 'response' ? 'response' : 'request';
        setActionKind(side === 'response' ? 'replace_response' : 'replace_request');
        setHeaderRows(
          hs.length
            ? hs.map<HeaderRow>((h) => ({ mode: modeOf(h), name: h.name ?? '', value: h.value ?? '', side }))
            : [blankHeader()],
        );
      }
    }
  }

  // ---- row mutations ----
  const patchRow = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const changeField = (i: number, field: string) => {
    const op0 = fieldFor(field)?.operators[0]?.value ?? '';
    patchRow(i, { field, operator: op0, key: '', value: '', values: [] });
  };
  const changeOperator = (i: number, operator: string) => patchRow(i, { operator, value: '', values: [] });

  const addRow = (logic: string) => {
    if (rows.length >= 5) return;
    setRows((rs) => {
      const next = rs.map((r, idx) => (idx === rs.length - 1 ? { ...r, logic } : r));
      return [...next, blankRow()];
    });
  };
  const removeRow = (i: number) => setRows((rs) => (rs.length <= 1 ? rs : rs.filter((_, idx) => idx !== i)));
  const setLogic = (rowIdx: number, logic: string) => patchRow(rowIdx - 1, { logic });

  // ---- header mutations ----
  const patchHeader = (i: number, patch: Partial<HeaderRow>) =>
    setHeaderRows((hs) => hs.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const changeHeaderMode = (i: number, mode: string) =>
    setHeaderRows((hs) => hs.map((h, idx) => (idx === i ? { ...h, mode, value: '' } : h)));
  const addHeader = () => setHeaderRows((hs) => [...hs, blankHeader()]);
  const removeHeader = (i: number) => setHeaderRows((hs) => (hs.length <= 1 ? hs : hs.filter((_, idx) => idx !== i)));

  // ---- build + validate (conditions) ----
  const opHasOptions = (op?: FirewallOperator) => op?.type === 'geo' || !!op?.options?.length || opIsList(op);
  const keyMissing = (r: Row) => !!fieldFor(r.field)?.input_value && !r.key.trim();
  const valueMissing = (r: Row) => {
    const op = opFor(r);
    if (!op || op.input_value === false) return false; // operator takes no value
    return opHasOptions(op) ? r.values.length === 0 : !r.value.trim();
  };
  const valueBadFormat = (r: Row) => {
    const op = opFor(r);
    if (!op || op.input_value === false || opHasOptions(op)) return false;
    if (!fieldIsIp(fieldFor(r.field)) || !r.value.trim()) return false;
    return !isIpAddress(r.value);
  };
  const rowComplete = (r: Row) =>
    !!r.field && !!r.operator && !keyMissing(r) && !valueMissing(r) && !valueBadFormat(r);

  const validRows = rows.filter((r) => r.field && r.operator);

  function buildConditions(): EdgeRuleCondition[] {
    return validRows.map((r, idx) => {
      const op = opFor(r);
      const hasOptions = opHasOptions(op);
      const cond: EdgeRuleCondition = { field: r.field, operator: r.operator };
      if (fieldFor(r.field)?.input_value && r.key.trim()) cond.key = r.key.trim();
      if (op?.input_value !== false) {
        if (hasOptions) cond.values = r.values;
        else cond.value = r.value.trim();
      }
      if (idx < validRows.length - 1) cond.logic = r.logic || 'and';
      return cond;
    });
  }

  // ---- build + validate (action) ----
  const headerNameUnsupported = (h: HeaderRow) => !!h.name.trim() && unsupportSet.has(h.name.trim().toLowerCase());
  const headerNameMissing = (h: HeaderRow) => !h.name.trim();
  const headerValueMissing = (h: HeaderRow) => h.mode !== 'remove' && !h.value.trim();
  const headerBad = (h: HeaderRow) => headerNameMissing(h) || headerNameUnsupported(h) || headerValueMissing(h);

  const redirectUrlBad = !redirectUrl.trim() || !isUrlish(redirectUrl);

  const actionValid = (() => {
    if (actionKind === 'redirect') return !redirectUrlBad;
    if (actionKind === 'force_https') return true;
    return headerRows.length >= 1 && !headerRows.some(headerBad);
  })();

  function buildAction(): EdgeRuleAction {
    if (actionKind === 'redirect') {
      const a: EdgeRuleAction = { type: 'redirect', url: redirectUrl.trim(), redirect_type: redirectType };
      if (redirectCode) a.code = Number(redirectCode);
      // Both types send the flag; static has no toggle, so it's always false.
      a.preserve_query_string = redirectType === 'dynamic' ? preserveQuery : false;
      return a;
    }
    if (actionKind === 'force_https') {
      return { type: 'forceHttps' };
    }
    if (actionKind === 'replace_legacy') {
      // Per-row request/response side; value is always a static literal.
      return {
        type: 'replace',
        headers: headerRows.map<EdgeHeaderValue>((h) => ({
          type: h.side === 'response' ? 'response' : 'request',
          name: h.name.trim(),
          value: h.value,
        })),
      };
    }
    // Replace request/response header — every row locked to the pill's side.
    // `expression` flags how the value is read: dynamic token / remove / static.
    const side = headerSide(actionKind);
    return {
      type: 'replace',
      headers: headerRows.map<EdgeHeaderValue>((h) => {
        if (h.mode === 'remove') return { type: side, name: h.name.trim(), value: '', expression: 'remove' };
        if (h.mode === 'dynamic') return { type: side, name: h.name.trim(), value: h.value, expression: 'dynamic' };
        return { type: side, name: h.name.trim(), value: h.value };
      }),
    };
  }

  const valid =
    !!name.trim() && rows.length >= 1 && rows.length <= 5 && rows.every(rowComplete) && actionValid;

  const create = useCreateEdgeRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule created' }); trackFeatureAction('waf', 'edge_rule', 'create'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateEdgeRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule updated' }); trackFeatureAction('waf', 'edge_rule', 'edit'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const pending = create.isPending || update.isPending;

  const submit = () => {
    setSubmitted(true);
    if (!valid) return;
    const input: EdgeRuleInput = {
      name: name.trim(),
      enable: rule ? rule.enable : true,
      action: buildAction(),
      conditions: buildConditions(),
    };
    if (!isEdit && templateId) input.template_id = templateId;
    if (isEdit && rule) update.mutate({ domain, id: rule.id, input });
    else create.mutate({ domain, input });
  };

  // Human-readable summary of the matching conditions.
  const summary = useMemo(() => {
    const parts = validRows.map((r) => {
      const f = fieldFor(r.field);
      const o = opFor(r);
      const isGeo = o?.type === 'geo';
      const v = isGeo
        ? r.values.map((x) => GEO_OPTIONS.find((g) => g.value === x)?.label ?? x).join(', ')
        : opIsList(o) || o?.options?.length ? r.values.join(', ') : r.value;
      return `${f?.label ?? r.field} ${o?.label ?? r.operator} "${v}"`;
    });
    if (!parts.length) return '';
    return parts.reduce((acc, part, idx) => (idx === 0 ? part : `${acc} ${validRows[idx - 1].logic || 'and'} ${part}`), '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const header = (
    <PageHeader
      icon={<Replace size={22} />}
      title={`${isEdit ? 'Edit' : 'Add'} HTTP header rewrite rule`}
      description="Match requests with a condition, then redirect or rewrite request/response headers."
      actions={<Button variant="secondary" startIcon={<ArrowLeft size={15} />} onClick={back}>Back</Button>}
    />
  );

  if (!domain) {
    return (
      <Container size="lg">{header}
        <Alert tone="warning" title="No domain selected" actions={<button onClick={back}>Back</button>}>
          Open this form from the HTTP Header Rewrite list so a domain is selected.
        </Alert>
      </Container>
    );
  }
  if (schemaQ.isError) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Couldn't load the rule builder" actions={<button onClick={() => schemaQ.refetch()}>Retry</button>}>
          The WAF service couldn't return the rule schema. Try again.
        </Alert>
      </Container>
    );
  }
  if (!seeded) {
    return <Container size="lg">{header}<Skeleton style={{ height: 460 }} /></Container>;
  }
  if (isEdit && !rule) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Rule not found" actions={<button onClick={back}>Back</button>}>This rule no longer exists.</Alert>
      </Container>
    );
  }

  const replaceActive = isReplaceKind(actionKind);

  return (
    <Container size="lg">
      {header}
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? 'Edit' : 'New'} header rewrite rule</CardTitle>
        </CardHeader>
        <CardBody>
          {/* Shared suggestions for every header-name input. */}
          {supportNames.length > 0 && (
            <datalist id={headerListId}>
              {supportNames.map((n) => <option key={n} value={n} />)}
            </datalist>
          )}
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Rule name">
              <Input value={name} placeholder="Rule name" disabled={pending} invalid={submitted && !name.trim()} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 360 }} />
            </Field>

            <div>
              <p className={styles.sectionTitle}>Rule configurations</p>
              <div className={styles.condList}>
                {rows.map((r, i) => {
                  const f = fieldFor(r.field);
                  const op = opFor(r);
                  const ops = f?.operators ?? [];
                  const isGeo = op?.type === 'geo';
                  const isList = opIsList(op);
                  const hasOptions = isGeo || isList || !!op?.options?.length;
                  const selectOptions = isGeo ? GEO_OPTIONS : isList ? [] : (op?.options ?? []).map((o) => ({ value: o, label: o }));
                  return (
                    <div key={i} className={styles.condRow}>
                      <div className={styles.condRail}>
                        {i === 0 ? (
                          <span className={styles.ifChip}>IF</span>
                        ) : (
                          <div className={styles.logicToggle}>
                            <button type="button" className={rows[i - 1].logic !== 'or' ? styles.logicOn : styles.logicOff} disabled={pending} onClick={() => setLogic(i, 'and')}>And</button>
                            <button type="button" className={rows[i - 1].logic === 'or' ? styles.logicOn : styles.logicOff} disabled={pending} onClick={() => setLogic(i, 'or')}>Or</button>
                          </div>
                        )}
                      </div>

                      <div className={styles.condBody}>
                        <div className={styles.condTop}>
                          <Select value={r.field} onValueChange={(v) => changeField(i, v)} disabled={pending}>
                            <SelectTrigger aria-label="Field" className={styles.condField}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {fields.map((fl) => <SelectItem key={fl.value} value={fl.value}>{fl.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {f?.input_value && (
                            <Input value={r.key} placeholder="Filter value" disabled={pending} invalid={submitted && keyMissing(r)} onChange={(e) => patchRow(i, { key: e.target.value })} aria-label="Filter value" className={styles.condFilter} />
                          )}
                          <IconButton variant="ghost" size="sm" aria-label="Remove condition" disabled={pending || rows.length <= 1} onClick={() => removeRow(i)}>
                            <Trash2 size={15} />
                          </IconButton>
                        </div>
                        {f?.description ? <span className={styles.condHint}>{f.description}</span> : null}
                        <div className={styles.condBottom}>
                          <Select value={r.operator} onValueChange={(v) => changeOperator(i, v)} disabled={pending || !ops.length}>
                            <SelectTrigger aria-label="Operator" className={styles.condOp}><SelectValue placeholder="Operator" /></SelectTrigger>
                            <SelectContent>
                              {ops.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {op?.input_value === false ? null : hasOptions ? (
                            <div className={styles.condValue}>
                              <MultiSelect
                                placeholder={isGeo ? 'Select countries…' : isList ? 'Add values…' : 'Value'}
                                options={selectOptions}
                                value={r.values}
                                disabled={pending}
                                invalid={submitted && valueMissing(r)}
                                creatable={isList}
                                onChange={(v) => patchRow(i, { values: v })}
                              />
                            </div>
                          ) : (
                            <Input value={r.value} placeholder={fieldIsIp(f) ? '192.0.2.10' : 'Value'} disabled={pending} invalid={submitted && (valueMissing(r) || valueBadFormat(r))} onChange={(e) => patchRow(i, { value: e.target.value })} aria-label="Value" className={styles.condValue} />
                          )}
                        </div>
                        {submitted && valueBadFormat(r) ? (
                          <span style={{ color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-xs, 12px)' }}>
                            Enter a valid IPv4 address (e.g. 192.0.2.10 or 192.0.2.0/24).
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles.addRow}>
                <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} disabled={pending || rows.length >= 5} onClick={() => addRow('and')}>And</Button>
                <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} disabled={pending || rows.length >= 5} onClick={() => addRow('or')}>Or</Button>
                {rows.length >= 5 ? <span className={styles.subHint}>Up to 5 conditions.</span> : null}
              </div>

              {summary ? <div className={styles.summary}>{summary}</div> : null}
            </div>

            <hr className={styles.divider} />

            {/* THEN — action type pills + the chosen action's config. */}
            <div className={styles.condRow}>
              <div className={styles.condRail}>
                <span className={styles.ifChip}>THEN</span>
              </div>
              <div className={styles.segment}>
                {ACTION_PILLS.map((p) => (
                  <Button
                    key={p.value}
                    size="sm"
                    variant={actionKind === p.value ? undefined : 'secondary'}
                    disabled={pending}
                    onClick={() => setActionKind(p.value)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>

            {actionKind === 'redirect' && (
              <div className={styles.grid2}>
                <Field label="Redirect type" hint="Static redirects to a fixed URL; dynamic can keep the query string.">
                  <Select value={redirectType} onValueChange={setRedirectType} disabled={pending}>
                    <SelectTrigger aria-label="Redirect type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REDIRECT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Status code">
                  <Select value={redirectCode} onValueChange={setRedirectCode} disabled={pending}>
                    <SelectTrigger aria-label="Status code"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REDIRECT_CODES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Redirect URL" hint="Where matched requests are sent.">
                  <Input value={redirectUrl} placeholder="https://example.com/new-path" disabled={pending} invalid={submitted && redirectUrlBad} onChange={(e) => setRedirectUrl(e.target.value)} />
                </Field>
                {redirectType === 'dynamic' && (
                  <Field label="Preserve query string" hint="Keep the original query string on redirect.">
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', paddingTop: 6 }}>
                      <Switch checked={preserveQuery} disabled={pending} aria-label="Preserve query string" onCheckedChange={setPreserveQuery} />
                      <span className={styles.subHint}>{preserveQuery ? 'Yes' : 'No'}</span>
                    </div>
                  </Field>
                )}
              </div>
            )}

            {actionKind === 'force_https' && (
              <p className={styles.actHint}>Matched requests on HTTP are redirected to HTTPS. No further configuration needed.</p>
            )}

            {replaceActive && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                {actionKind === 'replace_legacy' ? (
                  <p className={styles.actHint}>Pick a side per header, then set its value.</p>
                ) : (
                  <p className={styles.actHint}>
                    Rewrite {headerSide(actionKind) === 'response' ? 'response' : 'request'} headers — set a static or dynamic value, or remove the header.
                  </p>
                )}
                <div className={styles.condList}>
                  {headerRows.map((h, i) => {
                    const nameInput = (
                      <div className={styles.hName}>
                        <Input
                          list={supportNames.length > 0 ? headerListId : undefined}
                          value={h.name}
                          placeholder="Header name"
                          disabled={pending}
                          invalid={submitted && (headerNameMissing(h) || headerNameUnsupported(h))}
                          aria-label={`Header name ${i + 1}`}
                          onChange={(e) => patchHeader(i, { name: e.target.value })}
                        />
                        {submitted && headerNameUnsupported(h) ? (
                          <span style={{ color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-xs, 12px)' }}>
                            This header can't be rewritten.
                          </span>
                        ) : null}
                      </div>
                    );
                    const removeBtn = (
                      <IconButton variant="ghost" size="sm" aria-label="Remove header" disabled={pending || headerRows.length <= 1} onClick={() => removeHeader(i)}>
                        <Trash2 size={15} />
                      </IconButton>
                    );
                    return (
                      <div key={i} className={styles.condRow}>
                        <div className={styles.condRail}>
                          <span className={styles.ifChip}>And</span>
                        </div>
                        {actionKind === 'replace_legacy' ? (
                          <div className={styles.legacyBody}>
                            <div className={styles.segment}>
                              {HEADER_SIDES.map((s) => (
                                <Button
                                  key={s.value}
                                  size="sm"
                                  variant={(h.side || 'request') === s.value ? undefined : 'secondary'}
                                  disabled={pending}
                                  onClick={() => patchHeader(i, { side: s.value })}
                                >
                                  {s.label}
                                </Button>
                              ))}
                            </div>
                            <div className={styles.headerRow}>
                              {nameInput}
                              <div className={styles.valueCell}>
                                <Input
                                  value={h.value}
                                  placeholder="Header value"
                                  disabled={pending}
                                  invalid={submitted && headerValueMissing(h)}
                                  aria-label={`Header value ${i + 1}`}
                                  onChange={(e) => patchHeader(i, { value: e.target.value })}
                                />
                              </div>
                              {removeBtn}
                            </div>
                          </div>
                        ) : (
                          <div className={styles.headerRow}>
                            <div className={styles.headerMode}>
                              <Select value={h.mode} onValueChange={(v) => changeHeaderMode(i, v)} disabled={pending}>
                                <SelectTrigger aria-label="Header mode"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {HEADER_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            {nameInput}
                            <div className={styles.valueCell}>
                              {h.mode === 'remove' ? (
                                <span className={styles.subHint} style={{ paddingTop: 6 }}>Header will be removed.</span>
                              ) : h.mode === 'dynamic' ? (
                                <Select value={h.value} onValueChange={(v) => patchHeader(i, { value: v })} disabled={pending}>
                                  <SelectTrigger aria-label="Dynamic value" data-invalid={(submitted && headerValueMissing(h)) || undefined}>
                                    <SelectValue placeholder="Select dynamic value" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {dynamicValues.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  value={h.value}
                                  placeholder="Header value"
                                  disabled={pending}
                                  invalid={submitted && headerValueMissing(h)}
                                  aria-label={`Header value ${i + 1}`}
                                  onChange={(e) => patchHeader(i, { value: e.target.value })}
                                />
                              )}
                            </div>
                            {removeBtn}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className={styles.addRow}>
                  <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} disabled={pending} onClick={addHeader}>Add header</Button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', justifyContent: 'flex-end', marginTop: 'var(--vn-space-2)' }}>
              {submitted && !valid ? (
                <span style={{ marginRight: 'auto', color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-sm)' }}>
                  Please complete the highlighted fields.
                </span>
              ) : null}
              <Button variant="secondary" onClick={back} disabled={pending}>Cancel</Button>
              <Button onClick={submit} disabled={pending}>{isEdit ? 'Save changes' : 'Create'}</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}

export default WafEdgeRuleForm;
